import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, User, Mail, Phone, ArrowRight, Check, Loader, LogOut } from "lucide-react";

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");

  // Form states
  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [formError, setFormError] = useState("");

  // Check if user is already logged in from localStorage
  useEffect(() => {
    const loggedInUser = localStorage.getItem("user");
    if (loggedInUser) {
      const userData = JSON.parse(loggedInUser);
      setIsLoggedIn(true);
      setUserName(userData.name);
    }
  }, []);

  const handleLoginClick = () => {
    if (isLoggedIn) {
      setIsLogoutModalOpen(true);
    } else {
      setIsLoginModalOpen(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    setIsLoggedIn(false);
    setUserName("");
    setIsLogoutModalOpen(false);
  };

  const closeModal = () => {
    setIsLoginModalOpen(false);
    setIsNewUser(false);
    setOtpSent(false);
    setMobileNumber("");
    setEmail("");
    setName("");
    setOtp("");
    setOtpError("");
    setFormError("");
  };

  const handleSendOTP = () => {
    if (isNewUser && (!name || !email || !mobileNumber)) {
      setFormError("Please fill all required fields");
      return;
    }

    if (!isNewUser && !mobileNumber) {
      setFormError("Please enter your mobile number");
      return;
    }

    if (!/^\d{10}$/.test(mobileNumber)) {
      setFormError("Please enter a valid 10-digit mobile number");
      return;
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError("Please enter a valid email address");
      return;
    }

    setFormError("");
    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
      setOtpSent(true);
      console.log(`OTP sent to ${mobileNumber}`);
    }, 1500);
  };

  const handleVerifyOTP = () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);

      if (otp.length === 6) {
        const userData = {
          mobile: mobileNumber,
          name: isNewUser ? name : `User${mobileNumber.slice(-4)}`,
          email: email || "",
        };

        localStorage.setItem("user", JSON.stringify(userData));
        setIsLoggedIn(true);
        setUserName(userData.name);
        closeModal();
      } else {
        setOtpError("Invalid OTP. Please try again.");
      }
    }, 1500);
  };

  return (
    <header className="bg-gray-900 shadow-md">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Link to="/" aria-label="Wardha Mushroom Home">
              <img
                src="/images/finallogo.png"
                alt="Wardha Mushroom Logo"
                className="h-12 w-auto transition-transform transform hover:scale-105"
                loading="lazy"
              />
            </Link>
            <div className="flex flex-col">
              <Link to="/" className="text-3xl font-extrabold text-white hover:text-orange-400 transition-colors duration-300">
                Wardha Mushroom
              </Link>
              <span className="text-sm font-medium text-gray-300 hover:text-orange-400 transition-colors duration-300">
                🌿 Freshness You Can Trust 🌿
              </span>
            </div>
          </div>

          <button
            className="lg:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} className="text-white" /> : <Menu size={24} className="text-white" />}
          </button>

          <nav className="hidden lg:flex space-x-8">
            <Link to="/" className="hover:text-orange-400 text-gray-300">
              Home
            </Link>
            <Link to="/about" className="hover:text-orange-400 text-gray-300">
              About Us
            </Link>
            {/* ROUTE FIXED: pointing to /products */}
            <Link to="/products" className="hover:text-orange-400 text-gray-300">
              Products
            </Link>
            <Link to="/wholesale" className="hover:text-orange-400 text-gray-300">
              Wholesale
            </Link>
            <Link to="/global-trade" className="hover:text-orange-400 text-gray-300">
              Global Trade
            </Link>
            <Link to="/training" className="hover:text-orange-400 text-gray-300">
              Training
            </Link>
            <Link to="/blog" className="hover:text-orange-400 text-gray-300">
              Blog
            </Link>
            <Link to="/contact" className="hover:text-orange-400 text-gray-300">
              Contact Us
            </Link>
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <button
              className="px-6 py-2 rounded-full transition duration-300 flex items-center space-x-2"
              style={{ backgroundColor: "#FFA500", color: "white" }}
              onClick={handleLoginClick}
            >
              {isLoggedIn ? (
                <>
                  <User size={18} />
                  <span>{userName.split(' ')[0]}</span>
                </>
              ) : (
                "Login"
              )}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <nav id="mobile-menu" className="lg:hidden mt-4 space-y-2">
            <Link to="/" className="block py-2 text-gray-300 hover:text-orange-400" onClick={() => setIsMenuOpen(false)}>
              Home
            </Link>
            <Link to="/about" className="block py-2 text-gray-300 hover:text-orange-400" onClick={() => setIsMenuOpen(false)}>
              About Us
            </Link>
            {/* MOBILE ROUTE FIXED */}
            <Link to="/products" className="block py-2 text-gray-300 hover:text-orange-400" onClick={() => setIsMenuOpen(false)}>
              Products
            </Link>
            <Link to="/wholesale2" className="block py-2 text-gray-300 hover:text-orange-400" onClick={() => setIsMenuOpen(false)}>
              Wholesale
            </Link>
            <Link to="/global-trade" className="block py-2 text-gray-300 hover:text-orange-400" onClick={() => setIsMenuOpen(false)}>
              Global Trade
            </Link>
            <Link to="/training" className="block py-2 text-gray-300 hover:text-orange-400" onClick={() => setIsMenuOpen(false)}>
              Training
            </Link>
            <Link to="/blog" className="block py-2 text-gray-300 hover:text-orange-400" onClick={() => setIsMenuOpen(false)}>
              Blog
            </Link>
            <Link to="/contact" className="block py-2 text-gray-300 hover:text-orange-400" onClick={() => setIsMenuOpen(false)}>
              Contact Us
            </Link>
            <div className="flex items-center py-2">
              <button
                className="px-4 py-1 rounded-full text-white w-full text-center"
                style={{ backgroundColor: "#FFA500" }}
                onClick={() => { setIsMenuOpen(false); handleLoginClick(); }}
              >
                {isLoggedIn ? userName.split(' ')[0] : "Login"}
              </button>
            </div>
          </nav>
        )}
      </div>

      {isLoginModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white">
                {otpSent ? "Verify OTP" : isNewUser ? "Create Account" : "Login"}
              </h3>
              <button 
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-200"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {!otpSent ? (
                <div className="space-y-4">
                  {isNewUser && (
                    <>
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                          Full Name*
                        </label>
                        <div className="relative">
                          <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                          <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border border-gray-600 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-gray-700 text-white"
                            placeholder="Enter your full name"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                          Email Address*
                        </label>
                        <div className="relative">
                          <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                          <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border border-gray-600 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-gray-700 text-white"
                            placeholder="Enter your email"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div>
                    <label htmlFor="mobile" className="block text-sm font-medium text-gray-300 mb-1">
                      Mobile Number*
                    </label>
                    <div className="relative">
                      <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                      <input
                        type="tel"
                        id="mobile"
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        className="pl-10 pr-4 py-2 w-full border border-gray-600 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-gray-700 text-white"
                        placeholder="10-digit mobile number"
                        maxLength={10}
                      />
                    </div>
                  </div>

                  {formError && (
                    <p className="text-red-500 text-sm">{formError}</p>
                  )}

                  <button
                    onClick={handleSendOTP}
                    disabled={isLoading}
                    className="w-full py-2 px-4 text-white font-medium rounded-md shadow-sm transition duration-300 flex items-center justify-center"
                    style={{ backgroundColor: "#FFA500" }}
                  >
                    {isLoading ? (
                      <Loader size={20} className="animate-spin" />
                    ) : (
                      <>
                        <span>Send OTP</span>
                        <ArrowRight size={16} className="ml-2" />
                      </>
                    )}
                  </button>

                  <div className="text-center mt-4">
                    <button
                      onClick={() => setIsNewUser(!isNewUser)}
                      className="text-orange-500 hover:text-orange-300 text-sm font-medium"
                    >
                      {isNewUser ? "Already have an account? Login" : "New user? Create account"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-300 text-sm">
                    We've sent a 6-digit OTP to <span className="font-semibold">{mobileNumber}</span>
                  </p>

                  <div>
                    <label htmlFor="otp" className="block text-sm font-medium text-gray-300 mb-1">
                      Enter OTP
                    </label>
                    <input
                      type="text"
                      id="otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="px-4 py-2 w-full border border-gray-600 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-center tracking-widest text-lg bg-gray-700 text-white"
                      placeholder="******"
                      maxLength={6}
                    />
                  </div>

                  {otpError && (
                    <p className="text-red-500 text-sm">{otpError}</p>
                  )}

                  <button
                    onClick={handleVerifyOTP}
                    disabled={isLoading}
                    className="w-full py-2 px-4 text-white font-medium rounded-md shadow-sm transition duration-300 flex items-center justify-center"
                    style={{ backgroundColor: "#FFA500" }}
                  >
                    {isLoading ? (
                      <Loader size={20} className="animate-spin" />
                    ) : (
                      <>
                        <span>Verify & Login</span>
                        <Check size={16} className="ml-2" />
                      </>
                    )}
                  </button>

                  <div className="flex justify-between text-sm">
                    <button 
                      onClick={() => setOtpSent(false)}
                      className="text-gray-400 hover:text-gray-200"
                    >
                      Change number
                    </button>
                    <button 
                      onClick={handleSendOTP}
                      className="text-orange-500 hover:text-orange-300"
                      disabled={isLoading}
                    >
                      Resend OTP
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gray-700 px-6 py-3 text-center text-xs text-gray-400">
              By continuing, you agree to Wardha Mushroom's Terms of Service and Privacy Policy
            </div>
          </div>
        </div>
      )}

      {isLogoutModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
            <div className="p-5 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white">Logout Confirmation</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center mb-4">
                <LogOut size={24} className="text-orange-500 mr-3" />
                <p className="text-gray-300">Are you sure you want to logout?</p>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsLogoutModalOpen(false)}
                  className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 rounded-md text-white transition-colors"
                  style={{ backgroundColor: "#FFA500" }}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;